import { useState, useEffect, useRef } from 'react';
import RozetkaDashboard from './RozetkaDashboard';
import AdminPage from './pages/AdminPage';
import LandingPage from './pages/LandingPage';

const API = '';
const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || '';

const S = {
  page: { display:'flex', alignItems:'center', justifyContent:'center',
    minHeight:'100vh', background:'#0b1120', fontFamily:"'Inter',sans-serif" },
  card: { background:'#1a2236', padding:40, borderRadius:16,
    border:'1px solid #1e3a5f', width:360 },
  title: { fontSize:22, fontWeight:800, color:'#f1f5f9', marginBottom:4 },
  sub: { fontSize:13, color:'#94a3b8', marginBottom:24 },
  input: { width:'100%', padding:'10px 14px', marginBottom:12,
    borderRadius:10, background:'#111827', border:'1px solid #1e3a5f',
    color:'#f1f5f9', fontSize:14, boxSizing:'border-box', outline:'none' },
  btn: (color='#3b82f6', disabled=false) => ({
    width:'100%', padding:12, background: disabled ? '#1e3a5f' : color,
    color:'#fff', border:'none', borderRadius:10, fontSize:15,
    fontWeight:700, cursor: disabled ? 'default' : 'pointer',
    opacity: disabled ? 0.7 : 1, marginBottom:8 }),
  btnSec: { width:'100%', padding:10, background:'transparent',
    color:'#94a3b8', border:'1px solid #1e3a5f', borderRadius:10,
    fontSize:13, cursor:'pointer' },
  err: { color:'#f472b6', fontSize:13, marginBottom:12 },
  ok:  { color:'#34d399', fontSize:13, marginBottom:12 },
  sep: { textAlign:'center', color:'#334155', fontSize:12, margin:'12px 0',
    borderTop:'1px solid #1e3a5f', paddingTop:12 },
  link: { color:'#60a5fa', fontSize:13, cursor:'pointer', background:'none',
    border:'none', padding:0, display:'block', textAlign:'center', marginTop:8 },
};

// Cloudflare Turnstile widget
function Turnstile({ onToken }) {
  const ref = useRef(null);
  const widgetId = useRef(null);

  useEffect(() => {
    if (!TURNSTILE_SITE_KEY) { onToken('no-captcha'); return; }
    const load = () => {
      if (ref.current && window.turnstile && !widgetId.current) {
        widgetId.current = window.turnstile.render(ref.current, {
          sitekey: TURNSTILE_SITE_KEY,
          callback: onToken,
          'expired-callback': () => onToken(''),
          theme: 'dark',
          size: 'flexible',
        });
      }
    };
    if (window.turnstile) { load(); }
    else {
      const id = 'cf-turnstile-script';
      if (!document.getElementById(id)) {
        const s = document.createElement('script');
        s.id = id; s.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
        s.async = true; s.defer = true;
        s.onload = load;
        document.head.appendChild(s);
      } else {
        const interval = setInterval(() => { if (window.turnstile) { clearInterval(interval); load(); } }, 200);
      }
    }
    return () => {
      if (widgetId.current !== null && window.turnstile) {
        window.turnstile.remove(widgetId.current);
        widgetId.current = null;
      }
    };
  }, []); // eslint-disable-line

  if (!TURNSTILE_SITE_KEY) return null;
  return <div ref={ref} style={{ marginBottom: 12 }} />;
}

function LoginForm({ onLogin, onSwitch, onForgot }) {
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');
  const [cfToken, setCfToken] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    if (TURNSTILE_SITE_KEY && !cfToken) { setError('Пройдіть перевірку капчі'); return; }
    setLoading(true); setError('');
    const form = new FormData(e.target);
    try {
      const res = await fetch(`${API}/token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({
          username: form.get('email'),
          password: form.get('password'),
          client_id: cfToken,
        })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Помилка входу');
      onLogin(data.access_token);
    } catch (e) { setError(e.message); } finally { setLoading(false); }
  };

  return (
    <div style={S.page}>
      <div style={S.card}>
        <div style={S.title}>Likvix</div>
        <div style={S.sub}>Увійдіть до свого акаунту</div>
        <form onSubmit={submit}>
          <input name="email" type="email" placeholder="Email" required style={S.input} />
          <input name="password" type="password" placeholder="Пароль" required style={S.input} />
          <Turnstile onToken={setCfToken} />
          {error && <div style={S.err}>{error}</div>}
          <button type="submit" disabled={loading} style={S.btn()}>
            {loading ? 'Вхід...' : 'Увійти'}
          </button>
        </form>
        <button onClick={onForgot} style={S.link}>Забули пароль?</button>
        <div style={S.sep}>або</div>
        <button onClick={onSwitch} style={S.btnSec}>Зареєструватися</button>
      </div>
    </div>
  );
}

function RegisterForm({ onLogin, onSwitch }) {
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');
  const [cfToken, setCfToken] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    if (TURNSTILE_SITE_KEY && !cfToken) { setError('Пройдіть перевірку капчі'); return; }
    setLoading(true); setError('');
    const form = new FormData(e.target);
    if (form.get('password') !== form.get('password2')) {
      setError('Паролі не співпадають'); setLoading(false); return;
    }
    try {
      const res = await fetch(`${API}/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: form.get('email'),
          password: form.get('password'),
          shop_name: form.get('shop_name'),
          rozetka_client_id: form.get('rozetka_client_id') || undefined,
          rozetka_client_secret: form.get('rozetka_client_secret') || undefined,
          cf_token: cfToken,
        })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Помилка реєстрації');
      onLogin(data.access_token);
    } catch (e) { setError(e.message); } finally { setLoading(false); }
  };

  return (
    <div style={S.page}>
      <div style={{ ...S.card, maxHeight:'90vh', overflowY:'auto' }}>
        <div style={S.title}>Реєстрація</div>
        <div style={S.sub}>Створіть акаунт Likvix</div>
        <form onSubmit={submit}>
          <input name="email" type="email" placeholder="Email *" required style={S.input} />
          <input name="shop_name" placeholder="Назва магазину *" required style={S.input} />
          <input name="password" type="password" placeholder="Пароль * (мін. 6 символів)" required minLength={6} style={S.input} />
          <input name="password2" type="password" placeholder="Повторіть пароль *" required style={S.input} />
          <div style={{ height:1, background:'#1e3a5f', margin:'8px 0 12px' }} />
          <div style={{ fontSize:12, color:'#64748b', marginBottom:8 }}>
            API ключі Rozetka (можна додати пізніше в Налаштуваннях)
          </div>
          <input name="rozetka_client_id" placeholder="Client ID" style={S.input} />
          <input name="rozetka_client_secret" type="password" placeholder="Client Secret" style={S.input} />
          <Turnstile onToken={setCfToken} />
          {error && <div style={S.err}>{error}</div>}
          <button type="submit" disabled={loading} style={S.btn()}>
            {loading ? 'Реєстрація...' : 'Зареєструватися'}
          </button>
        </form>
        <div style={S.sep}>вже є акаунт?</div>
        <button onClick={onSwitch} style={S.btnSec}>Увійти</button>
      </div>
    </div>
  );
}

function ForgotPasswordForm({ onBack }) {
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');
  const [ok, setOk]           = useState('');
  const [cfToken, setCfToken] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    if (TURNSTILE_SITE_KEY && !cfToken) { setError('Пройдіть перевірку капчі'); return; }
    setLoading(true); setError(''); setOk('');
    const form = new FormData(e.target);
    try {
      const res = await fetch(`${API}/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: form.get('email'), cf_token: cfToken })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Помилка');
      setOk('Якщо email зареєстровано — лист надіслано. Перевірте пошту.');
    } catch (e) { setError(e.message); } finally { setLoading(false); }
  };

  return (
    <div style={S.page}>
      <div style={S.card}>
        <div style={S.title}>Відновлення паролю</div>
        <div style={S.sub}>Введіть email — надішлемо посилання</div>
        <form onSubmit={submit}>
          <input name="email" type="email" placeholder="Email" required style={S.input} />
          <Turnstile onToken={setCfToken} />
          {error && <div style={S.err}>{error}</div>}
          {ok    && <div style={S.ok}>{ok}</div>}
          {!ok && (
            <button type="submit" disabled={loading} style={S.btn()}>
              {loading ? 'Надсилаємо...' : 'Надіслати посилання'}
            </button>
          )}
        </form>
        <button onClick={onBack} style={S.link}>← Назад до входу</button>
      </div>
    </div>
  );
}

function ResetPasswordForm({ token, onDone }) {
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');
  const [ok, setOk]           = useState(false);
  const [cfToken, setCfToken] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    if (TURNSTILE_SITE_KEY && !cfToken) { setError('Пройдіть перевірку капчі'); return; }
    setLoading(true); setError('');
    const form = new FormData(e.target);
    if (form.get('password') !== form.get('password2')) {
      setError('Паролі не співпадають'); setLoading(false); return;
    }
    try {
      const res = await fetch(`${API}/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password: form.get('password'), cf_token: cfToken })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Помилка');
      setOk(true);
    } catch (e) { setError(e.message); } finally { setLoading(false); }
  };

  if (ok) return (
    <div style={S.page}>
      <div style={S.card}>
        <div style={S.title}>Готово!</div>
        <div style={{ color:'#34d399', marginBottom:24 }}>Пароль успішно змінено.</div>
        <button onClick={onDone} style={S.btn()}>Увійти</button>
      </div>
    </div>
  );

  return (
    <div style={S.page}>
      <div style={S.card}>
        <div style={S.title}>Новий пароль</div>
        <div style={S.sub}>Введіть новий пароль для вашого акаунту</div>
        <form onSubmit={submit}>
          <input name="password" type="password" placeholder="Новий пароль (мін. 6)" required minLength={6} style={S.input} />
          <input name="password2" type="password" placeholder="Повторіть пароль" required style={S.input} />
          <Turnstile onToken={setCfToken} />
          {error && <div style={S.err}>{error}</div>}
          <button type="submit" disabled={loading} style={S.btn()}>
            {loading ? 'Збереження...' : 'Змінити пароль'}
          </button>
        </form>
      </div>
    </div>
  );
}

function SettingsPage({ apiToken, onBack }) {
  const [loading, setLoading] = useState(false);
  const [info, setInfo]       = useState(null);
  const [error, setError]     = useState('');
  const [ok, setOk]           = useState('');
  const [epToken, setEpToken]   = useState('');
  const [epLoading, setEpLoading] = useState(false);
  const [epError, setEpError]   = useState('');
  const [epOk, setEpOk]         = useState('');
  const [npKey, setNpKey]       = useState('');
  const [npLoading, setNpLoading] = useState(false);
  const [npError, setNpError]   = useState('');
  const [npOk, setNpOk]         = useState('');

  useEffect(() => {
    fetch(`${API}/api/me`, { headers: { Authorization: 'Bearer ' + apiToken } })
      .then(r => r.json()).then(setInfo).catch(console.error);
  }, [apiToken]);

  const save = async (e) => {
    e.preventDefault();
    setLoading(true); setError(''); setOk('');
    const form = new FormData(e.target);
    try {
      const res = await fetch(`${API}/api/me/credentials`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + apiToken },
        body: JSON.stringify({
          rozetka_client_id: form.get('client_id'),
          rozetka_client_secret: form.get('client_secret'),
        })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Помилка збереження');
      setOk('Збережено! Синхронізація розпочнеться автоматично.');
    } catch (e) { setError(e.message); } finally { setLoading(false); }
  };

  const saveEpicentr = async (e) => {
    e.preventDefault();
    if (!epToken.trim()) return;
    setEpLoading(true); setEpError(''); setEpOk('');
    try {
      const res = await fetch(`${API}/api/me/epicentr-credentials`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + apiToken },
        body: JSON.stringify({ token: epToken.trim() }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Помилка збереження');
      setInfo(i => ({ ...i, has_epicentr: true }));
      setEpToken('');
      setEpOk('Токен збережено! Синхронізація замовлень Epicentr розпочнеться автоматично.');
    } catch (e) { setEpError(e.message); } finally { setEpLoading(false); }
  };

  const saveNovaPoshta = async (e) => {
    e.preventDefault();
    if (!npKey.trim()) return;
    setNpLoading(true); setNpError(''); setNpOk('');
    try {
      const res = await fetch(`${API}/api/me/novaposhta-credentials`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + apiToken },
        body: JSON.stringify({ api_key: npKey.trim() }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Помилка збереження');
      setInfo(i => ({ ...i, has_novaposhta: true }));
      setNpKey('');
      setNpOk('Ключ збережено! Трекінг ТТН Nova Poshta оновлюватиметься автоматично.');
    } catch (e) { setNpError(e.message); } finally { setNpLoading(false); }
  };

  return (
    <div style={S.page}>
      <div style={S.card}>
        <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:20 }}>
          <button onClick={onBack} style={{ background:'transparent', border:'none', color:'#94a3b8', cursor:'pointer', fontSize:18, padding:0 }}>←</button>
          <div style={S.title}>Налаштування</div>
        </div>
        {info && (
          <div style={{ background:'#111827', borderRadius:10, padding:'12px 14px', marginBottom:20 }}>
            <div style={{ fontSize:12, color:'#64748b' }}>Акаунт</div>
            <div style={{ fontSize:14, color:'#f1f5f9', fontWeight:600, marginTop:2 }}>{info.email}</div>
            <div style={{ fontSize:12, color:'#94a3b8' }}>{info.shop_name}</div>
          </div>
        )}
        <div style={{ fontSize:14, fontWeight:700, color:'#f1f5f9', marginBottom:12 }}>API ключі Rozetka Seller</div>
        <form onSubmit={save}>
          <input name="client_id" placeholder="Client ID" required style={S.input} defaultValue={info?.rozetka_client_id || ''} />
          <input name="client_secret" type="password" placeholder="Client Secret" required style={S.input} />
          {error && <div style={S.err}>{error}</div>}
          {ok    && <div style={S.ok}>{ok}</div>}
          <button type="submit" disabled={loading} style={S.btn()}>
            {loading ? 'Збереження...' : 'Зберегти та запустити синхронізацію'}
          </button>
        </form>

        <div style={{ borderTop:'1px solid #1e293b', margin:'24px 0' }} />

        <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:12 }}>
          <div style={{ fontSize:14, fontWeight:700, color:'#f1f5f9' }}>Epicentr Merchant API</div>
          {info?.has_epicentr
            ? <span style={{ fontSize:11, background:'#14532d', color:'#4ade80', borderRadius:20, padding:'2px 8px', fontWeight:600 }}>Підключено ✓</span>
            : <span style={{ fontSize:11, background:'#1e293b', color:'#94a3b8', borderRadius:20, padding:'2px 8px' }}>Не підключено</span>
          }
        </div>
        <div style={{ fontSize:12, color:'#64748b', marginBottom:12 }}>
          Bearer-токен з кабінету продавця Epicentr (Merchant API). Замовлення синхронізуються кожні 30 хв автоматично.
        </div>
        <form onSubmit={saveEpicentr}>
          <input
            value={epToken}
            onChange={e => setEpToken(e.target.value)}
            placeholder={info?.has_epicentr ? 'Новий токен (залиш порожнім якщо без змін)' : 'mp_xxxxxxxxxxxxxxxx...'}
            type="password"
            style={S.input}
          />
          {epError && <div style={S.err}>{epError}</div>}
          {epOk    && <div style={S.ok}>{epOk}</div>}
          <button type="submit" disabled={epLoading || !epToken.trim()} style={S.btn()}>
            {epLoading ? 'Збереження...' : info?.has_epicentr ? 'Оновити токен' : 'Підключити Epicentr'}
          </button>
        </form>

        <div style={{ borderTop:'1px solid #1e293b', margin:'24px 0' }} />

        <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:12 }}>
          <div style={{ fontSize:14, fontWeight:700, color:'#f1f5f9' }}>Nova Poshta API</div>
          {info?.has_novaposhta
            ? <span style={{ fontSize:11, background:'#14532d', color:'#4ade80', borderRadius:20, padding:'2px 8px', fontWeight:600 }}>Підключено ✓</span>
            : <span style={{ fontSize:11, background:'#1e293b', color:'#94a3b8', borderRadius:20, padding:'2px 8px' }}>Не підключено</span>
          }
        </div>
        <div style={{ fontSize:12, color:'#64748b', marginBottom:12 }}>
          API-ключ із кабінету Nova Poshta. Статуси доставки по ТТН (прибуття, отримання, повернення) оновлюються автоматично.
        </div>
        <form onSubmit={saveNovaPoshta}>
          <input
            value={npKey}
            onChange={e => setNpKey(e.target.value)}
            placeholder={info?.has_novaposhta ? 'Новий ключ (залиш порожнім якщо без змін)' : 'API-ключ Nova Poshta'}
            type="password"
            style={S.input}
          />
          {npError && <div style={S.err}>{npError}</div>}
          {npOk    && <div style={S.ok}>{npOk}</div>}
          <button type="submit" disabled={npLoading || !npKey.trim()} style={S.btn()}>
            {npLoading ? 'Збереження...' : info?.has_novaposhta ? 'Оновити ключ' : 'Підключити Nova Poshta'}
          </button>
        </form>

        <button onClick={onBack} style={S.btnSec}>Назад до дашборду</button>
      </div>
    </div>
  );
}

export default function App() {
  const [token, setToken]               = useState(localStorage.getItem('crm_token'));
  const [view, setView]                 = useState('landing');
  const [hasCredentials, setHasCreds]   = useState(true);
  const [tenantId, setTenantId]         = useState(null);
  const [hasEpicentr, setHasEpicentr]   = useState(false);
  const [resetToken, setResetToken]     = useState(null);

  // Check for reset token in URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const rt = params.get('token');
    if (rt) {
      setResetToken(rt);
      setView('reset-password');
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  useEffect(() => {
    if (!token) return;
    fetch(`${API}/api/me`, { headers: { Authorization: 'Bearer ' + token } })
      .then(r => { if (r.status === 401) { logout(); return null; } return r.json(); })
      .then(d => { if (d) { setHasCreds(d.has_credentials); setTenantId(d.tenant_id); setHasEpicentr(d.has_epicentr || false); } })
      .catch(console.error);
    // Refresh token if it expires within 1 day
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      if (payload.exp && (payload.exp - Date.now() / 1000) < 86400) {
        fetch(`${API}/api/auth/refresh`, { method: 'POST', headers: { Authorization: 'Bearer ' + token } })
          .then(r => r.ok ? r.json() : null)
          .then(d => { if (d?.access_token) { localStorage.setItem('crm_token', d.access_token); setToken(d.access_token); } })
          .catch(console.error);
      }
    } catch (_) {}
  }, [token]); // eslint-disable-line

  const login = (tok) => { localStorage.setItem('crm_token', tok); setToken(tok); setView('dashboard'); };
  const logout = () => { localStorage.removeItem('crm_token'); setToken(null); setView('landing'); };

  if (view === 'reset-password') return <ResetPasswordForm token={resetToken} onDone={() => setView('login')} />;
  if (view === 'forgot')         return <ForgotPasswordForm onBack={() => setView('login')} />;

  if (!token) {
    if (view === 'register') return <RegisterForm onLogin={login} onSwitch={() => setView('login')} />;
    if (view === 'login') return <LoginForm onLogin={login} onSwitch={() => setView('register')} onForgot={() => setView('forgot')} />;
    return <LandingPage onGetStarted={() => setView('register')} onLogin={() => setView('login')} />;
  }

  if (view === 'settings') return <SettingsPage apiToken={token} onBack={() => setView('dashboard')} />;
  if (view === 'admin') return <AdminPage apiBaseUrl={API} token={token} onBack={() => setView('dashboard')} />;

  return (
    <RozetkaDashboard
      apiBaseUrl={API}
      onLogout={logout}
      onOpenSettings={() => setView('settings')}
      onOpenAdmin={() => setView('admin')}
      hasCredentials={hasCredentials}
      tenantId={tenantId}
      hasEpicentr={hasEpicentr}
    />
  );
}
